<script setup lang="ts">
import { computed, onMounted, reactive, ref, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { usersApi } from '@/services/api/users';
import { organizationsApi, type Organization } from '@/services/api/organizations';
import { useNotifier } from '@/composables/notifier';
import { usePasswordStrength } from '@/composables/usePasswordStrength';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const notify = useNotifier();

const form = reactive({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
  terms: false,
});

const passwordStrength = usePasswordStrength(toRef(form, 'password'));
const submitting = ref(false);
const invitedOrganization = ref<Organization | null>(null);

const valid = computed(
  () =>
    form.first_name.length > 0 &&
    form.last_name.length > 0 &&
    form.email.length > 0 &&
    form.password.length > 0 &&
    form.password === form.confirm_password &&
    form.terms,
);

onMounted(async () => {
  const rawToken = route.params.invite_token;
  const token = Array.isArray(rawToken) ? rawToken[0] : rawToken;
  if (typeof token === 'string' && token.length > 0) {
    try {
      invitedOrganization.value = await organizationsApi.getByInviteToken(token);
    } catch {
      notify.warn('Not a valid invitation');
      await router.replace({ name: 'dashboard' });
    }
  }
});

async function submit(): Promise<void> {
  if (!valid.value || submitting.value) return;
  submitting.value = true;
  try {
    const payload = {
      name: `${form.first_name} ${form.last_name}`,
      email: form.email,
      password: form.password,
      organization_id: invitedOrganization.value?.id ?? null,
    };
    await usersApi.create(payload);
    await auth.login(form.email, form.password);
    await router.replace({ name: 'dashboard' });
  } catch {
    notify.error('Registration failed. Please try again.');
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <form class="register" novalidate @submit.prevent="submit">
    <h2 class="register__title">Create your personal account</h2>

    <p v-if="invitedOrganization" class="register__invite-note">
      Joining <strong>{{ invitedOrganization.name }}</strong>
    </p>

    <label class="register__field" for="first-name">
      <input
        id="first-name"
        v-model="form.first_name"
        name="first_name"
        type="text"
        placeholder="First Name"
        required
      />
    </label>

    <label class="register__field" for="last-name">
      <input
        id="last-name"
        v-model="form.last_name"
        name="last_name"
        type="text"
        placeholder="Last Name"
        required
      />
    </label>

    <label class="register__field" for="email">
      <input
        id="email"
        v-model="form.email"
        name="email"
        type="email"
        placeholder="Email Address"
        required
      />
    </label>

    <label class="register__field" for="password">
      <input
        id="password"
        v-model="form.password"
        name="password"
        type="password"
        placeholder="Password"
        required
      />
      <meter min="1" max="4" :value="passwordStrength" low="2" high="3" optimum="4" />
    </label>

    <label class="register__field" for="confirm_password">
      <input
        id="confirm_password"
        v-model="form.confirm_password"
        name="confirm_password"
        type="password"
        placeholder="Confirm Password"
        required
      />
    </label>

    <label class="register__terms">
      <input v-model="form.terms" type="checkbox" />
      <span>I agree to the EMSL Terms of Service</span>
    </label>

    <button type="submit" class="register__submit" :disabled="!valid || submitting">
      {{ submitting ? 'Creating…' : 'Get started' }}
    </button>
  </form>
</template>

<style scoped>
.register {
  max-width: 28rem;
  margin: 4rem auto;
  padding: 2rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.register__title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  text-align: center;
}
.register__invite-note {
  margin: 0;
  text-align: center;
  color: #2563eb;
}
.register__field input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.register__field meter {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
}
.register__terms {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}
.register__submit {
  padding: 0.625rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
}
.register__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
